// @ts-strict-ignore
import {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import {
  Box,
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  Grid,
  Icon,
  IconName,
  Label,
  Modal,
  MultiSelect,
  Text,
  TextArea,
  useSnackBar,
} from '@usealloy/component-library';
import type { Option } from '@usealloy/component-library';
import {
  formatFileSize, handleFileChange, handleUploadDocument, updateFiles,
} from './documentHelper';
import { UserContext } from '~/dashboard/context';
import find from 'lodash/find';
import {
  ChooseFileInput,
  ChooseFileLabel,
  CloudUploadIconWrapper,
  FileNameWrapper,
  FileRowGrid,
  TargetDropBox,
  TruncatedFileNameText,
  UploadInfo,
} from '~/components/inputs/ChooseFileInputStyles';
import type { Entity } from '~/entities/types';

type EntityForUpload = Pick<Entity, 'entity_name' | 'entity_token'>;

interface DocumentUploadModalProps {
  caseToken?: string;
  documentTypes: Array<{
    document_type_id: number;
    type: string;
  }>;
  entities: {
    [entity_token: string]: EntityForUpload;
  };
  evaluationToken?: string;
  investigationToken?: string;
  isOpen: boolean;
  onClose: () => void;
  onUpload: () => void;
}

export const DocumentUploadModal = ({
  caseToken,
  documentTypes,
  entities,
  evaluationToken,
  investigationToken,
  isOpen,
  onClose,
  onUpload,
}: DocumentUploadModalProps) => {
  const { agent, typhonDomain } = useContext(UserContext);
  const { isEmployee } = agent || {};
  const [uploading, setUploading] = useState(false);
  const [isCaseEvidence, setIsCaseEvidence] = useState(false);
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [entity, setEntity] = useState<EntityForUpload>({
    entity_name: null,
    entity_token: null,
  });
  const defaultFileType = find(documentTypes, { type: 'other' });

  const isMultiEntityContext = Object.values(entities).length > 1;

  useEffect(() => {
    if (!isMultiEntityContext) {
      const currentEntity = Object.values(entities)[0];
      setEntity(currentEntity);
    } else if (isMultiEntityContext) {
      // temp handling to always use the first entity until we update the modal to include an entity selector
      // Entity selector ticket: https://team-alloy.atlassian.net/browse/OTF-110
      setEntity(Object.values(entities)[0]);
    }
  }, [entities, isMultiEntityContext]);

  const handleDragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleClose = () => {
    onClose();
    setFiles([]);
  };

  const handleFileDrop = item => {
    setIsDragging(false);
    if (item) {
      const uploaded = item.files;
      updateFiles(defaultFileType, uploaded, setFiles);
    }
  };

  const [, drop] = useDrop(() => ({
    accept: [NativeTypes.FILE],
    drop(item) {
      handleFileDrop(item);
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [handleFileDrop]);

  const addSnackBar = useSnackBar();

  const onSubmit = useCallback(
    event => {
      const caseMetaData = isCaseEvidence && { case_token: caseToken };
      const successHandler = (() => {
        addSnackBar({
          title: `Documents successfully uploaded to ${entity?.entity_name || entity?.entity_token}`,
          type: 'success',
        });
        if (onUpload) {
          onUpload();
        } else {
          window.location.reload();
        }
      });

      handleUploadDocument({
        addSnackBar,
        agentEmail: agent.email,
        caseMetaData,
        documentTypes,
        entity,
        evaluationToken,
        event,
        files,
        investigationToken,
        onClose,
        setUploading,
        successHandler,
        typhonDomain,
      });
    }, [
      addSnackBar,
      agent.email,
      caseToken,
      documentTypes,
      entity,
      evaluationToken,
      files,
      investigationToken,
      isCaseEvidence,
      onClose,
      onUpload,
      typhonDomain,
    ]);

  const handleDeleteFile = index => {
    setFiles(currentFiles => {
      const newFiles = [...currentFiles];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  return (
    <Modal
      header={`${entity?.entity_name || entity?.entity_token}: Upload Documents`}
      isOpen={isOpen}
      onClose={handleClose}
      primaryAction={(
        <Button
          onClick={onSubmit}
          variant={ButtonVariant.primary}
          loading={uploading}
          disabled={files.length === 0}
        >
          Upload
        </Button>
      )}
      secondaryAction={(
        <Button onClick={handleClose}>Cancel</Button>
      )}
    >
      <Box direction="column" alignItems="flex-start" gap={4}>
        <Text>
          {`Upload your documents below to add them to this ${investigationToken ? 'Investigation' : 'Entity'}`}
        </Text>
        <TargetDropBox
          className={isDragging ? 'drag-over' : ''}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          ref={drop}
          direction="column"
          gap={3}
        >
          <CloudUploadIconWrapper>
            <Icon icon={IconName.CloudUpload} />
          </CloudUploadIconWrapper>
          <Text lineHeight={4}>
            {'Drop your file here, or '}
            <ChooseFileLabel htmlFor="document-upload">
              browse files
            </ChooseFileLabel>
          </Text>
          <UploadInfo>
            {'Max File Size: '}
            <UploadInfo weight={700}>50MB</UploadInfo>
          </UploadInfo>
          <UploadInfo>
            {'Supported file types: '}
            <UploadInfo weight={700}>JPG, PNG, PDF, HEIF, MSG</UploadInfo>
          </UploadInfo>
          <ChooseFileInput
            id="document-upload"
            className="file"
            type="file"
            multiple
            name="document_upload"
            formEncType="multipart/form-data"
            onChange={event => handleFileChange(defaultFileType, event, setFiles)}
          />
        </TargetDropBox>
        {caseToken && (
          <Checkbox
            label="These files are evidence"
            checked={isCaseEvidence}
            onChange={event => { setIsCaseEvidence(event.target.checked); }}
            disabled={uploading || files.length === 0}
          />
        )}
        {files.map((file, index) => (
          <FileRowGrid
            key={`file-${file.fileObj.name}-${file.fileObj.lastModified}-${file.fileObj.size}`}
            colGap={4}
            style={{ minWidth: '100%' }}
          >
            <Grid.Item columns={{ sm: 4, md: 4, lg: 4 }}>
              <Box direction="column" alignItems="flex-start" gap={2}>
                <Label>Uploaded</Label>
                <FileNameWrapper justifyContent="flex-start">
                  <Box justifyContent="space-between" gap={0}>
                    <Box gap={0} justifyContent="flex-start" style={{ maxWidth: 'calc(100% - var(--cl-space-10))' }}>
                      <TruncatedFileNameText>
                        {file.fileObj.name.slice(0, -8)}
                      </TruncatedFileNameText>
                      <Text>{file.fileObj.name.slice(-8)}</Text>
                      <Text style={{ color: 'var(--cl-gray-700)' }}>
                        &nbsp;{`• ${formatFileSize(file.fileObj.size)}`}
                      </Text>
                    </Box>
                    <Button
                      icon={IconName.Trash}
                      onClick={() => { handleDeleteFile(index); }}
                      size={ButtonSize.small}
                      variant={ButtonVariant.tertiary}
                    />
                  </Box>
                </FileNameWrapper>
              </Box>
            </Grid.Item>
            <Grid.Item columns={{ sm: 2, md: 2, lg: 2 }}>
              <MultiSelect
                defaultValue={documentTypes.filter(({ type }) => type === 'other').map(type => ({
                  label: `${type.type.charAt(0).toUpperCase()}${type.type.slice(1)}`.replaceAll('_', ' '),
                  value: type.document_type_id,
                }))}
                isMulti={false}
                label="Type"
                onChange={({ value }: Option) => {
                  setFiles(currentFiles => {
                    const newFiles = [...currentFiles];
                    newFiles.splice(index, 1, {
                      ...currentFiles[index],
                      type: (parseInt(value, 10)),
                    });
                    return newFiles;
                  });
                }}
                options={documentTypes.map(type => ({
                  label: `${type.type.charAt(0).toUpperCase()}${type.type.slice(1)}`.replaceAll('_', ' '),
                  value: String(type.document_type_id),
                }))}
                selectedOptions={(
                  documentTypes
                    .filter(({ document_type_id }) => document_type_id === file.type)
                    .map(type => ({
                      label: `${type.type.charAt(0).toUpperCase()}${type.type.slice(1)}`.replaceAll('_', ' '),
                      value: String(type.document_type_id),
                    }))
                )}
              />
            </Grid.Item>
            <Grid.Item columns={{ sm: 4, md: 4, lg: 4 }}>
              <TextArea
                disabled={isEmployee}
                label="Note"
                onChange={({ target: { value } }) => {
                  setFiles(currentFiles => {
                    const newFiles = [...currentFiles];
                    newFiles.splice(index, 1, {
                      ...currentFiles[index],
                      notes: value,
                    });
                    return newFiles;
                  });
                }}
                placeholder={isEmployee ? 'Notes are disabled for employees' : ''}
                rows={1}
                style={{
                  resize: 'vertical',
                  height: 'var(--cl-space-10)',
                  minHeight: 'var(--cl-space-10)',
                }}
                value={file.note}
              />
            </Grid.Item>
          </FileRowGrid>
        ))}
      </Box>
    </Modal>
  );
};
